'use client';

import * as React from 'react';
import { DayPicker } from 'react-day-picker';
import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import { enUS, fr, de, sk, cs } from 'date-fns/locale';
import { format, type Locale } from 'date-fns';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

export const localeMapping: Record<string, Locale> = {
  'en-US': enUS,
  'fr-FR': fr,
  'de-DE': de,
  sk: sk,
  'sk-SK': sk,
  cs: cs,
  'cs-CZ': cs,
  // Add other locales as needed
};

const NAV_BUTTON_CLASS =
  'absolute top-0 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[6px] border border-border bg-white text-foreground/70 transition-colors hover:bg-secondary hover:text-foreground disabled:pointer-events-none disabled:opacity-40';

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  const localeCode = useLocaleStore(s => s.currentLocale);

  const locale = localeMapping[localeCode] || enUS; // Default to enUS if locale not found

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      locale={locale}
      captionLayout={props.captionLayout || 'dropdown'}
      navLayout={props.navLayout || 'around'}
      className={cn('p-4', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row gap-4',
        month: 'relative flex flex-col gap-3',
        month_caption: 'flex h-7 items-center justify-center gap-1 px-9',
        caption_label: 'flex items-center gap-1 text-sm font-semibold',
        dropdowns: 'flex items-center gap-2',
        dropdown_root:
          'relative flex cursor-pointer items-center rounded-[6px] border border-border bg-white px-2 py-1 text-sm font-medium shadow-sm transition-colors hover:bg-secondary/60',
        dropdown: 'absolute inset-0 cursor-pointer opacity-0',
        button_previous: cn(NAV_BUTTON_CLASS, 'left-0'),
        button_next: cn(NAV_BUTTON_CLASS, 'right-0'),
        month_grid: 'w-full border-collapse mt-2',
        weekdays: 'flex',
        weekday: 'w-9 text-center text-[0.75rem] font-medium text-muted-foreground',
        week: 'flex w-full mt-1',
        day: 'relative h-9 w-9 rounded-[6px] p-0 text-center text-sm focus-within:relative focus-within:z-20',
        day_button: cn(
          'inline-flex h-9 w-9 items-center justify-center rounded-[6px] text-sm font-normal',
          'transition-colors hover:bg-primary/20 aria-selected:opacity-100',
        ),
        range_start: 'day-range-start',
        range_end: 'day-range-end',
        selected: 'bg-primary text-white hover:bg-primary hover:text-white',
        today: 'ring-1 ring-inset ring-primary/60 font-semibold',
        outside: 'text-muted-foreground opacity-50',
        disabled: 'text-muted-foreground opacity-40',
        range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) =>
          orientation === 'left' ? (
            <ChevronLeftIcon className="h-4 w-4" />
          ) : (
            <ChevronRightIcon className="h-4 w-4" />
          ),
      }}
      weekStartsOn={props.weekStartsOn ?? 1}
      formatters={{
        formatWeekdayName: date => format(date, 'EE', { locale }),
      }}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
